import React, { useState, useEffect } from 'react';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

interface CallStepInterfaceProps {
  connectionId: string;
  currentUserId?: string;
}

export const CallStepInterface: React.FC<CallStepInterfaceProps> = ({ 
  connectionId,
  currentUserId = 'user123' 
}) => {
  const [callActive, setCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [otherUserStatus, setOtherUserStatus] = useState<'waiting' | 'connected' | 'disconnected'>('waiting');
  const [scheduledTime, setScheduledTime] = useState<Date | null>(null);
  const [showCallConfirmation, setShowCallConfirmation] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [proposedDateTime, setProposedDateTime] = useState('');
  const [googleMeetUrl, setGoogleMeetUrl] = useState<string | null>(null);

  useEffect(() => {
    // Listen to call status updates
    const unsubscribe = onSnapshot(
      doc(db, 'connectionFlows', connectionId),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as any;
          setScheduledTime(data.scheduledCallTime?.toDate() || null);
          // Optioneel veld: googleMeetUrl – kan later via Flow of backend worden gezet
          setGoogleMeetUrl(data.googleMeetUrl || null);
        }
      }
    );

    return unsubscribe;
  }, [connectionId]);

  // Call timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (callActive) {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [callActive]);

  const requestCallStart = () => {
    setShowCallConfirmation(true);
  };

  const confirmCallStart = async () => {
    setShowCallConfirmation(false);
    setCallActive(true);
    setCallDuration(0);
    
    // Update call status in database
    await updateDoc(doc(db, 'connectionFlows', connectionId), {
      callStatus: 'active',
      callStartTime: new Date()
    });

    // Simulate other user connecting after 2 seconds
    setTimeout(() => {
      setOtherUserStatus('connected');
    }, 2000);
  };

  const cancelCallStart = () => {
    setShowCallConfirmation(false);
  };

  const openScheduleDialog = () => {
    setShowScheduleDialog(true);
    // Set default to tomorrow at 2 PM
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(14, 0, 0, 0);
    setProposedDateTime(tomorrow.toISOString().slice(0, 16));
  };

  const closeScheduleDialog = () => {
    setShowScheduleDialog(false);
  };

  const scheduleCall = async () => {
    if (!proposedDateTime) return;
    
    const scheduledDate = new Date(proposedDateTime);
    
    await updateDoc(doc(db, 'connectionFlows', connectionId), {
      scheduledCallTime: scheduledDate,
      callStatus: 'scheduled'
    });
    
    setScheduledTime(scheduledDate);
    setShowScheduleDialog(false);
  };

  const endCall = async () => {
    setCallActive(false);
    
    await updateDoc(doc(db, 'connectionFlows', connectionId), {
      callStatus: 'ended',
      callEndTime: new Date(),
      callDuration: callDuration
    });

    // Auto transition to video step after call ends
    setTimeout(async () => {
      await updateDoc(doc(db, 'connectionFlows', connectionId), {
        currentStep: 'video',
        stepStartTime: new Date(),
        stepDuration: 5
      });
    }, 1000);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    // Here you would integrate with actual audio API
  };

  const toggleSpeaker = () => {
    setIsSpeakerOn(!isSpeakerOn);
    // Here you would integrate with actual audio API
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const canStartCall = () => {
    if (!scheduledTime) return false;
    const now = new Date();
    const timeDiff = Math.abs(now.getTime() - scheduledTime.getTime()) / (1000 * 60); // in minutes
    return timeDiff <= 5; // Can start call 5 minutes before/after scheduled time
  };

  return (
    <div className="h-full flex flex-col">
      <div className="bg-green-50 p-4 rounded-lg mb-4">
        <h3 className="font-semibold text-green-800 mb-2">Telefoongesprek</h3>
        <p className="text-green-600 text-sm">
          Voer een telefonisch gesprek van 5 minuten. Dit helpt jullie elkaar beter te leren kennen voordat jullie verder gaan.
        </p>
      </div>

      {/* Scheduled Time Display or Schedule Button */}
      {scheduledTime ? (
        <div className="bg-blue-50 p-3 rounded-lg mb-4">
          <div className="text-sm text-blue-600">
            <strong>Geplande tijd:</strong> {scheduledTime.toLocaleString('nl-NL')}
          </div>
          {!canStartCall() && (
            <div className="text-sm text-orange-600 mt-1">
              Het gesprek kan gestart worden 5 minuten voor/na de geplande tijd
            </div>
          )}
        </div>
      ) : (
        <div className="bg-yellow-50 p-3 rounded-lg mb-4">
          <div className="text-sm text-yellow-700 mb-2">
            <strong>Geen gesprek gepland</strong>
          </div>
          <button
            onClick={openScheduleDialog}
            className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-sm"
          >
            📅 Plan Gesprek
          </button>
        </div>
      )}

      {/* Call Interface */}
      <div className="flex-1 flex flex-col items-center justify-center">
        {!callActive ? (
          // Pre-call state
          <div className="text-center">
            <div className="w-32 h-32 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <span className="text-4xl">📞</span>
            </div>
            
            <h4 className="text-xl font-semibold mb-2">Klaar om te bellen?</h4>
            <p className="text-gray-600 mb-6">
              Start het telefoongesprek wanneer jullie beiden klaar zijn
            </p>

            <button
              onClick={requestCallStart}
              disabled={!canStartCall()}
              className={`px-6 py-3 rounded-lg font-medium ${
                canStartCall()
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {canStartCall() ? 'Start Gesprek' : 'Wacht op geplande tijd'}
            </button>

            {/* Google Meet integratie – eenvoudige knop die een bestaande Meet-link opent */}
            <div className="mt-4">
              <button
                onClick={() => {
                  if (googleMeetUrl) {
                    window.open(googleMeetUrl, '_blank', 'noopener');
                  }
                }}
                disabled={!googleMeetUrl}
                className={`px-6 py-2 rounded-lg font-medium text-sm ${
                  googleMeetUrl
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                {googleMeetUrl ? '📹 Start Google Meet call' : 'Geen Google Meet link ingesteld'}
              </button>
            </div>
          </div>
        ) : (
          // Active call state
          <div className="text-center">
            <div className="w-40 h-40 bg-green-500 rounded-full flex items-center justify-center mb-6 animate-pulse">
              <span className="text-5xl">📞</span>
            </div>
            
            <h4 className="text-xl font-semibold mb-2">Gesprek Actief</h4>
            
            <div className="text-3xl font-mono text-green-600 mb-2">
              {formatDuration(callDuration)}
            </div>
            
            <div className="mb-6">
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                otherUserStatus === 'connected' 
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                <div className={`w-2 h-2 rounded-full mr-2 ${
                  otherUserStatus === 'connected' ? 'bg-green-500' : 'bg-yellow-500'
                }`}></div>
                {otherUserStatus === 'connected' ? 'Beide verbonden' : 'Verbinding maken...'}
              </div>
            </div>
            
            {/* Call Controls */}
            <div className="flex gap-4 mb-6">
              <button
                onClick={toggleMute}
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isMuted 
                    ? 'bg-red-500 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {isMuted ? '🔇' : '🎤'}
              </button>
              
              <button
                onClick={toggleSpeaker}
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isSpeakerOn 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {isSpeakerOn ? '🔊' : '🔉'}
              </button>
              
              <button
                onClick={endCall}
                className="w-12 h-12 rounded-full bg-red-500 text-white hover:bg-red-600 flex items-center justify-center"
              >
                📴
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Call Tips */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h5 className="font-medium text-gray-800 mb-2">Tips voor het gesprek:</h5>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Stel jezelf kort voor</li>
          <li>• Bespreek jullie verwachtingen</li>
          <li>• Plan alvast de videocall die hierna komt</li>
          <li>• Houd het luchtig en professioneel</li>
        </ul>
      </div>

      {/* Call Confirmation Dialog */}
      {showCallConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="text-center">
              <div className="text-4xl mb-4">📞</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Telefoongesprek Starten
              </h3>
              <p className="text-gray-600 mb-4">
                Weet je zeker dat je het telefoongesprek wilt starten? Dit zal een 5-minuten gesprek initiëren.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700">
                  <strong>Tip:</strong> Zorg ervoor dat jullie beiden klaar zijn voordat je start!
                </p>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={cancelCallStart}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={confirmCallStart}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  Ja, Start Gesprek
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Call Dialog */}
      {showScheduleDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="text-center">
              <div className="text-4xl mb-4">📅</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Plan Telefoongesprek
              </h3>
              <p className="text-gray-600 mb-4">
                Kies een datum en tijd voor het telefoongesprek
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Datum en Tijd
                </label>
                <input
                  type="datetime-local"
                  value={proposedDateTime}
                  onChange={(e) => setProposedDateTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700">
                  <strong>Tip:</strong> Het gesprek kan 5 minuten voor/na de geplande tijd gestart worden
                </p>
              </div>
              
              <div className="flex space-x-4">
                <button
                  onClick={closeScheduleDialog}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={scheduleCall}
                  disabled={!proposedDateTime}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Plan Gesprek
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


